<template>
    <h2 class="font-600 font-md mt-0 w-10/12 font-inter font-semibold md:w-full legacy-md:mt-3">Merge File</h2>
    <p class="relative mb-12 mt-2 text-base">Please review and select the data you would like to keep.</p>

    <div class="merge-table-grid gap-x-5 font-inter tracking-normal md:text-lg">
        <span></span>
        <span class="pb-4 pt-2 font-inter font-semibold">New File</span>
        <span class="pb-4 pt-2 font-inter font-semibold">Early Deposit</span>
        <MergeRow
            v-for="(row, fieldName) in mergeTableData"
            :key="fieldName"
            v-bind="row"
            @selected="setTableData(fieldName, $event)"
        />
        <MergeRow header-text="Clients">
            <p class="col-span-2 py-6">All clients associated with this Early Deposit will be added to the file.</p>
        </MergeRow>
        <MergeRow header-text="Notification Recipients">
            <p class="col-span-2 py-6">All notification recipients on this Early Deposit will be added to the file.</p>
        </MergeRow>
    </div>

    <footer class="mt-4 grid gap-y-2 md:mt-[max(1.5rem,5vw)] md:flex md:justify-end md:gap-x-4">
        <DsButton
            size="md"
            variant="secondary"
            @click="$emit('prevStep')"
        >
            Back
        </DsButton>
        <span class="flex-1"></span>
        <DsButton
            size="md"
            variant="secondary"
            @click="emit('confirmNoMerge')"
        >
            Create New File
        </DsButton>
        <DsButton
            v-if="!isSubmitting"
            :disabled="isSubmitDisabled"
            size="md"
            variant="primary"
            @click="cloneAndMergeFormData"
        >
            Merge File
        </DsButton>
        <div
            v-else
            class="px-8"
        >
            <LoadingSpinner size-classes="w-10 h-10" />
        </div>
    </footer>
</template>

<script setup lang="ts">
import type { EarlyDeposit, MergeRowProps } from '@/api/interfaces/early-deposit.types';
import type { SimilarFilesProps } from '@/company-portal/add-view-file';
import {
    mapCompanyBranchIdsToNames,
    pruneDuplicateClients,
    unzipClientsToFormArrays,
    zipClientsFromFormArrays,
} from '@/company-portal/add-view-file';
import LoadingSpinner from '@/components/Icons/LoadingSpinner.vue';
import DsButton from '@ds/button/DsButton.vue';
import { computed, reactive, ref } from 'vue';
import MergeRow from './MergeRow.vue';

const props = defineProps<
    SimilarFilesProps & {
        emd: EarlyDeposit;
        closeAndUnmount: () => void;
    }
>();

const emit = defineEmits<{
    confirmNoMerge: [];
    nextStep: [FormData];
    prevStep: [];
}>();

const setTableData = (fieldName: keyof typeof mergeTableData, value: string) => {
    mergeTableData[fieldName].selectedValue = value;
};

const isSubmitting = ref(false);
const cloneAndMergeFormData = () => {
    isSubmitting.value = true;
    const clonedFormData = new FormData();
    const fileEntries = [...props.fullFileFormData];

    for (const [key, val] of fileEntries) {
        const strVal = key in mergeTableData ? `${mergeTableData[key as keyof typeof mergeTableData].selectedValue}` : val;
        clonedFormData.append(key, strVal);
    }

    const fileClients = zipClientsFromFormArrays(props.fullFileFormData);
    const numFileClients = fileClients.length;
    const netNewEmdClients = pruneDuplicateClients(props.emd.clients, fileClients);
    const emdClientArrays = unzipClientsToFormArrays(netNewEmdClients);
    const numEmdClients = netNewEmdClients.length;

    for (let i = 0; i < numEmdClients; i++) {
        const nextClientIndex = i + numFileClients; // offset to not clobber any existing client indexes on the file FormData
        clonedFormData.set(`email[${nextClientIndex}]`, emdClientArrays.email[i] ?? '');
        clonedFormData.set(`first[${nextClientIndex}]`, emdClientArrays.first[i] ?? '');
        clonedFormData.set(`last[${nextClientIndex}]`, emdClientArrays.last[i] ?? '');
        clonedFormData.set(`id[${nextClientIndex}]`, emdClientArrays.id[i] ?? '');
        clonedFormData.set(`phone[${nextClientIndex}]`, emdClientArrays.phone[i] ?? '');
        clonedFormData.set(`role[${nextClientIndex}]`, emdClientArrays.role[i] ?? '');
    }

    const fileNotificationRecipients = props.fullFileFormData.getAll('fileNotificationUserIds[]');
    props.emd.notifying_users.forEach(recip => {
        const emdRecipId = `${recip.id}`;
        if (!fileNotificationRecipients.includes(emdRecipId)) {
            clonedFormData.append('fileNotificationUserIds[]', emdRecipId);
        }
    });

    // submitFormWith resolves a Promise (new flow) or returns a jqXHR (legacy); both are thenable.
    Promise.resolve(props.submitFormWith(props.emd.id, clonedFormData)).finally(props.closeAndUnmount);
};

const isSubmitDisabled = computed<boolean>(() => {
    return Object.values(mergeTableData).some(row => !row.selectedValue);
});

const createReactiveMergeTableData = () => {
    const { company: companyOnFile, branch: branchOnFile } = mapCompanyBranchIdsToNames();
    const emd = props.emd;
    const file = {
        address: `${props.fullFileFormData.get('address')}`,
        company_id: `${props.fullFileFormData.get('company_id')}`,
        branch_id: `${props.fullFileFormData.get('branch_id')}`,
    };

    const propertyRow: MergeRowProps = {
        headerText: 'Property',
        fileText: file.address,
        emdText: emd.address,
    };

    const companyRow: MergeRowProps = {
        headerText: 'Company',
        fileText: companyOnFile,
        fileValue: file.company_id,
        emdText: emd.branch.title.name,
        emdValue: `${emd.branch.title.id}`,
    };

    const branchRow: MergeRowProps = {
        headerText: 'Branch',
        fileText: branchOnFile,
        fileValue: file.branch_id,
        emdText: emd.branch.name,
        emdValue: `${emd.branch.id}`,
    };

    return {
        address: propertyRow,
        company_id: companyRow,
        branch_id: branchRow,
    } as const;
};
const mergeTableData = reactive(createReactiveMergeTableData());
</script>

<style lang="scss" scoped>
.merge-table-grid {
    display: grid;
    grid-template-columns: minmax(max-content, 1fr) 2fr 2fr;
}
</style>
